// FILE: src/hooks/useRestaurantContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import { auth, db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";

// ✅ IMPORTANT: default MUST be null so we can detect missing Provider
const RestaurantContext = createContext(null);

export function RestaurantProvider({ children }) {
  const [restaurantId, setRestaurantId] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubDoc = null;

    // ✅ Safety: never allow infinite loading
    let safetyTimer = setTimeout(() => {
      console.warn(
        "[RestaurantContext] Loading took too long. Forcing loading=false (check Provider + Firestore rules)."
      );
      setLoading(false);
    }, 8000);

    const clearSafety = () => {
      if (safetyTimer) {
        clearTimeout(safetyTimer);
        safetyTimer = null;
      }
    };

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      // reset safety timer each auth change
      clearSafety();
      safetyTimer = setTimeout(() => {
        console.warn(
          "[RestaurantContext] Loading took too long after auth change. Forcing loading=false."
        );
        setLoading(false);
      }, 8000);

      // If logged out
      if (!user) {
        if (unsubDoc) {
          unsubDoc();
          unsubDoc = null;
        }
        setRestaurantId(null);
        setRestaurant(null);
        setLoading(false);
        clearSafety();
        return;
      }

      const id = user.uid;
      setRestaurantId(id);
      setLoading(true);

      // Clean up any previous restaurant listener
      if (unsubDoc) {
        unsubDoc();
        unsubDoc = null;
      }

      const ref = doc(db, "restaurants", id);

      unsubDoc = onSnapshot(
        ref,
        (snap) => {
          if (snap.exists()) {
            setRestaurant({ id: snap.id, ...snap.data() });
          } else {
            setRestaurant(null);
          }
          setLoading(false);
          clearSafety();
        },
        (err) => {
          console.error("RestaurantContext snapshot error:", err);
          setRestaurant(null);
          setLoading(false);
          clearSafety();
        }
      );
    });

    // Clean up both listeners on unmount
    return () => {
      if (unsubAuth) unsubAuth();
      if (unsubDoc) unsubDoc();
      clearSafety();
    };
  }, []);

  const value = { restaurantId, restaurant, loading };

  // IMPORTANT: no JSX here – use createElement so .js is valid
  return React.createElement(RestaurantContext.Provider, { value }, children);
}

export default function useRestaurantContext() {
  const ctx = useContext(RestaurantContext);

  // ✅ Now this will correctly throw if Provider is missing anywhere
  if (ctx == null) {
    throw new Error(
      "useRestaurantContext must be used inside a <RestaurantProvider>"
    );
  }

  return ctx;
}
